import { Fragment, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getResourcePath } from '@spotahome/soyuz/client';

import {
  getPoiRequest,
  setPoiRequest,
  deletePoiRequest
} from '@spotahome/marketplace-common/src/api/poi-client';
import PlacesAutocomplete from '@spotahome/marketplace-common/src/components/PlacesAutocomplete';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import './InteractiveMap.scss';

const BIG_MARKER = getResourcePath(
  '/bundles/sahapp/images/ui/big-marker-selected.svg'
);
const POI_MARKER_PATH =
  'https://static.spotahome.com/map/marker-point-of-interest.svg';

const PLACE_SELECTED_EVENT_CATEGORY = 'interactive-map-add-reference-point';
const INITIAL_LISTING_ZOOM = 5;

const sendOpenEvent = () => {
  SoyuzAnalytics.sendGA4Event('interactive-map', { action: 'open-map' });
};

const sendSearchEvent = place => {
  const { name, lat, lng } = place;
  if (name && lat && lng) {
    SoyuzAnalytics.sendGA4Event(PLACE_SELECTED_EVENT_CATEGORY, {
      place: name,
      coord: `${lat},${lng}`
    });
  }
};

const fitToBoth = (map, propertyLatLng, poi) => {
  if (!poi) {
    map.setCenter(propertyLatLng);
    return;
  }

  const bounds = new window.woosmap.map.LatLngBounds();
  bounds.extend(propertyLatLng);
  bounds.extend({ lat: poi.lat, lng: poi.lng });
  map.fitBounds(bounds);
};

const InteractiveMap = ({ mapId, coords, areaSlug, countryCode }) => {
  const [pointOfInterest, setPointOfInterest] = useState(null);
  const map = useRef(null);
  const propertyMarker = useRef(null);
  const poiMarker = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const poiResponse = await getPoiRequest(areaSlug);
        setPointOfInterest(poiResponse.data.poi);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in POI request', error);
      }
    })();
  }, []);

  useEffect(() => {
    const container = document.getElementById(mapId);
    if (!container) return;

    const [lng, lat] = coords;
    const propertyLatLng = { lat, lng };

    map.current = new window.woosmap.map.Map(container, {
      center: propertyLatLng,
      zoom: INITIAL_LISTING_ZOOM
    });

    propertyMarker.current = new window.woosmap.map.Marker({
      position: propertyLatLng,
      map: map.current,
      icon: { url: BIG_MARKER }
    });

    sendOpenEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current) return;

    if (poiMarker.current) {
      poiMarker.current.setMap(null);
      poiMarker.current = null;
    }

    if (pointOfInterest) {
      poiMarker.current = new window.woosmap.map.Marker({
        position: { lat: pointOfInterest.lat, lng: pointOfInterest.lng },
        map: map.current,
        icon: { url: POI_MARKER_PATH }
      });
    }

    const [lng, lat] = coords;
    fitToBoth(map.current, { lat, lng }, pointOfInterest);
  }, [pointOfInterest]);

  const handlePlaceSelected = place => {
    if (place) {
      setPointOfInterest(place);
      setPoiRequest(areaSlug, place);
      sendSearchEvent(place);
    } else {
      setPointOfInterest(null);
      deletePoiRequest(areaSlug);
    }
  };

  const coordsObj = {
    lat: parseFloat(coords[1]),
    lng: parseFloat(coords[0])
  };

  return (
    <Fragment>
      <div className="interactive-map__header">
        <PlacesAutocomplete
          flat
          boundCoords={coordsObj}
          pointOfInterest={pointOfInterest}
          onPlaceSelected={handlePlaceSelected}
          countryCode={countryCode}
        />
      </div>
      <div
        className="interactive-map__map interactive-map__map--woosmap"
        id={mapId}
      />
    </Fragment>
  );
};

InteractiveMap.propTypes = {
  coords: PropTypes.arrayOf(PropTypes.number).isRequired,
  mapId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  areaSlug: PropTypes.string.isRequired
};

export default InteractiveMap;
